import React, { useState } from "react";
import { PlayButton, shuffled } from "../../grammar/shared";
import { GhostButton, PrimaryButton, Prompt } from "./shared";

function shuffledDifferent(tokens) {
  if (tokens.length < 2) return tokens.map((t, i) => ({ t, i }));
  for (let k = 0; k < 8; k++) {
    const s = shuffled(tokens.map((t, i) => ({ t, i })));
    if (s.some((x, pos) => x.i !== pos)) return s;
  }
  return tokens.map((t, i) => ({ t, i })).reverse();
}

/**
 * OrderSentenceItem — rebuild a dialogue line from shuffled word chips.
 * Tap chips to place them (RTL row), Undo / Clear to fix, Check when every
 * chip is placed. On a miss the correct order is revealed underneath.
 */
export default function OrderSentenceItem({ question, onAnswer, audio }) {
  const { tokens, sentence_en: en, sentence_ar: full } = question.data;
  const [bank] = useState(() => shuffledDifferent(tokens));
  const [placed, setPlaced] = useState([]); // indices into bank
  const [result, setResult] = useState(null); // true | false

  const place = (bankIdx) => {
    if (result !== null || placed.includes(bankIdx)) return;
    setPlaced((p) => [...p, bankIdx]);
  };
  const check = () => {
    if (result !== null) return;
    const built = placed.map((i) => bank[i].t);
    const ok = built.join(" ") === tokens.join(" ");
    setResult(ok);
    if (audio.audioAllowed) audio.play(full);
    onAnswer({ correct: ok, answer: built.join(" ") });
  };

  return (
    <>
      <Prompt>{question.prompt_en}</Prompt>
      {en && <p className="text-sm text-center mb-4" style={{ color: "var(--parchment-dim)" }}>{en}</p>}

      <div dir="rtl" className="flex flex-wrap gap-2 justify-center min-h-[60px] px-3 py-3 rounded-xl mb-4"
           style={{ background: "var(--ink)", border: `1px solid ${result === null ? "var(--panel-line)" : result ? "var(--teal)" : "#dc5a5a"}` }}>
        {placed.length === 0 && (
          <span className="text-sm self-center" style={{ color: "var(--parchment-dim)" }} dir="ltr">Tap the words below in order</span>
        )}
        {placed.map((i, pos) => (
          <button key={`${i}-${pos}`} type="button" disabled={result !== null}
                  onClick={() => setPlaced((p) => p.filter((x) => x !== i))}
                  className="px-3 py-1.5 min-h-[44px] rounded-lg font-arabic text-2xl"
                  style={{ background: "var(--gold-bg)", border: "1px solid var(--gold-dim)", color: "var(--gold-bright)" }}>
            {bank[i].t}
          </button>
        ))}
      </div>

      <div dir="rtl" className="flex flex-wrap gap-2 justify-center mb-4">
        {bank.map((x, i) => {
          const used = placed.includes(i);
          return (
            <button key={i} type="button" disabled={used || result !== null} onClick={() => place(i)}
                    className="px-3 py-1.5 min-h-[44px] rounded-lg font-arabic text-2xl transition-all hover:brightness-110 disabled:cursor-not-allowed"
                    style={used
                      ? { background: "transparent", border: "1px dashed var(--panel-line)", color: "var(--parchment-dim)", opacity: 0.5 }
                      : { background: "var(--ink)", border: "1px solid var(--panel-line)", color: "var(--parchment)" }}>
              {x.t}
            </button>
          );
        })}
      </div>

      {result === null ? (
        <div className="flex items-center justify-center gap-2">
          <GhostButton onClick={() => setPlaced((p) => p.slice(0, -1))} disabled={placed.length === 0}>Undo</GhostButton>
          <GhostButton onClick={() => setPlaced([])} disabled={placed.length === 0}>Clear</GhostButton>
          <PrimaryButton onClick={check} disabled={placed.length !== tokens.length}>Check</PrimaryButton>
        </div>
      ) : (
        <div className="flex items-center justify-center gap-3">
          <p dir="rtl" lang="ar" className="font-arabic text-3xl leading-[1.7] m-0" style={{ color: result ? "var(--teal)" : "var(--parchment)" }}>
            {full}
          </p>
          {audio.audioAllowed && <PlayButton text={full} playingText={audio.playingText} onPlay={audio.play} />}
        </div>
      )}
    </>
  );
}
